import React, { useCallback, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import ReactTooltip from 'react-tooltip';
import { Link, useHistory } from 'react-router-dom';
import { doc, setDoc, updateDoc } from 'firebase/firestore';
import { differenceInDays } from 'date-fns';
import { DB, Realtime } from '~/services/firebase';
import { useAuth } from '~/hooks/auth';
import { useToast } from '~/hooks/toast';

import getInitialLettersOfName from '~/utils/getInitialLettersOfName';
import { useBanners } from '~/hooks/banners';
import { useCategories } from '~/hooks/categories';
import { useProducts } from '~/hooks/products';
import ICompany from '~/interfaces/ICompany';

import { NewsInfoModalPropsRef } from '~/components/NewsInfoModal';
import { bff } from '~/services/bff';
import packageJson from '../../../package.json';
import Notifications from './Notifications';
import { CashButton } from '~/components/Cash/CashButton';
import { ChatbotWhatsAppSetup } from '~/components/ChatbotWhatsAppSetup';
import InstallPWAButton from '~/components/InstallPWAButton';

// import logo from '~/assets/logo-color.png';

interface DashbaordProps {
  containerMessage?: string;
}

const { REACT_APP_FIREBASE_PROJECT, REACT_APP_CHATBOT_WPP } = process.env;

console.log({ REACT_APP_CHATBOT_WPP });

const Dashboard: React.FC<DashbaordProps> = ({
  containerMessage,
  children,
}) => {
  const newInfoModalRef = useRef<NewsInfoModalPropsRef>(null);

  const history = useHistory();
  const { user, payments, company, updateCompany, signOut } = useAuth();
  const { addToast } = useToast();
  const { clearContext: clearCtxBanners } = useBanners();
  const { clearContext: clearCtxCategories } = useCategories();
  const { clearContext: clearCtxProducts } = useProducts();

  const isAdmin = useMemo(() => {
    return user.isAdmin;
  }, [user]);

  const path = useMemo(() => {
    return history.location.pathname;
  }, [history]);

  const initials = useMemo(() => {
    return getInitialLettersOfName(user.name);
  }, [user.name]);

  const dueDateDays = useMemo(() => {
    if (!company || !company.due_date) return 99;
    if (payments.length === 0) return -1;

    const dueDate = new Date();
    dueDate.setDate(company.due_date);

    const today = new Date();

    const days = differenceInDays(dueDate, today);

    const itsPaid = payments.filter(
      item =>
        item.year === dueDate.getFullYear() &&
        item.month === dueDate.getMonth() &&
        item.status === true,
    );

    if (itsPaid.length > 0) return 99;

    return days;
  }, [company, payments]);

  const updateStatusRealtime = useCallback(
    async (status: boolean) => {
      const ref = Realtime.ref(`status/${company.uid}`);
      await ref.set({ status });
    },
    [company],
  );

  const handleCompanyStatus = useCallback(async () => {
    const updated = { opened: !company.opened };

    updateCompany({
      ...company,
      opened: updated.opened,
    });

    await updateStatusRealtime(updated.opened);
    await updateDoc(doc(DB, 'companies', company.uid), updated);
    await bff.delete(`/companies/${REACT_APP_FIREBASE_PROJECT}`);

    addToast({
      type: updated.opened ? 'success' : 'info',
      description: updated.opened
        ? 'Seu sistema está aberto e pronto para receber pedidos!'
        : 'Atenção! Seu sistema está fechado e não poderá receber pedidos!',
    });
  }, [company, updateCompany, addToast, updateStatusRealtime]);

  const handleOnSignOut = useCallback(() => {
    clearCtxBanners();
    clearCtxCategories();
    clearCtxProducts();
    updateCompany({} as ICompany);
    signOut();
  }, [
    clearCtxBanners,
    clearCtxCategories,
    clearCtxProducts,
    updateCompany,
    signOut,
  ]);

  const handleGoToPayments = useCallback(() => {
    history.replace({
      pathname: '/settings',
      state: { tab: 'monthlyPayment' },
    });
  }, [history]);

  const handleOpenCash = useCallback(async () => {
    if (company.uid) {
      const parseData = {
        ...company,
        cashOpenedAt: new Date(),
      };
      await setDoc(doc(DB, 'companies', company.uid), parseData);
      await bff.delete(`/companies/${REACT_APP_FIREBASE_PROJECT}`);
      updateCompany(parseData);

      addToast({
        type: 'success',
        description: 'O caixa foi aberto com sucesso!',
      });
    }
  }, [company, addToast, updateCompany]);

  return (
    <>
      <Helmet>
        <link
          rel="stylesheet"
          href={`${process.env.PUBLIC_URL}/assets/css/dashlite.css`}
        />
      </Helmet>

      <div className="nk-app-root">
        <div className="nk-apps-sidebar is-theme">
          <ReactTooltip place="right" effect="solid" />
          {/* <div className="nk-apps-brand">
            <Link to="/" className="logo-link">
              <img className="logo-light logo-img" src={logo} alt="logo" />
              <img className="logo-dark logo-img" src={logo} alt="logo-dark" />
            </Link>
          </div> */}
          <div className="nk-sidebar-element">
            <div className="nk-sidebar-body">
              <div className="nk-sidebar-content" data-simplebar>
                <div className="nk-sidebar-menu">
                  <ul className="nk-menu apps-menu">
                    {/* <li className="nk-menu-item">
                      <Link
                        to="/"
                        className="nk-menu-link"
                        title="Analytics Dashboard"
                      >
                        <span className="nk-menu-icon">
                          <em className="icon ni ni-dashboard" />
                        </span>
                      </Link>
                    </li> */}
                    <li
                      data-tip="Pedidos"
                      className={`nk-menu-item ${
                        (path === '/' || path === '/orders') && 'active'
                      }`}
                    >
                      <Link
                        to="/orders"
                        className="nk-menu-link"
                        title="Pedidos"
                      >
                        <span className="nk-menu-icon">
                          <em className="icon ni ni-invest" />
                        </span>
                      </Link>
                    </li>

                    {company.sell_on_credit === '1' && isAdmin && (
                      <li
                        data-tip="Fiados"
                        className={`nk-menu-item ${
                          path === '/buyoncredit' && 'active'
                        }`}
                      >
                        <Link
                          to="/buyoncredit"
                          className="nk-menu-link"
                          title="Fiados"
                        >
                          <span className="nk-menu-icon">
                            <em className="icon ni ni-list-thumb" />
                          </span>
                        </Link>
                      </li>
                    )}

                    {isAdmin && (
                      <>
                        <li
                          data-tip="Pedidos de Mesa"
                          className={`nk-menu-item ${
                            path === '/table-orders' && 'active'
                          }`}
                        >
                          <Link
                            to="/table-orders"
                            className="nk-menu-link"
                            title="Pedidos de Mesa"
                          >
                            <span className="nk-menu-icon">
                              <em className="icon ni ni-clipboad-check" />
                            </span>
                          </Link>
                        </li>
                        <li
                          data-tip="Cardápios"
                          className={`nk-menu-item ${
                            path === '/menus' && 'active'
                          }`}
                        >
                          <Link
                            to="/menus"
                            className="nk-menu-link"
                            title="Cardápios"
                          >
                            <span className="nk-menu-icon">
                              <em className="icon ni ni-todo" />
                            </span>
                          </Link>
                        </li>
                        <li
                          data-tip="Categorias"
                          className={`nk-menu-item ${
                            path === '/categories' && 'active'
                          }`}
                        >
                          <Link
                            to="/categories"
                            className="nk-menu-link"
                            title="Categoiras"
                          >
                            <span className="nk-menu-icon">
                              <em className="icon ni ni-tags" />
                            </span>
                          </Link>
                        </li>
                        <li
                          data-tip="Produtos"
                          className={`nk-menu-item ${
                            path === '/products' && 'active'
                          }`}
                        >
                          <Link
                            to="/products"
                            className="nk-menu-link"
                            title="Produtos"
                          >
                            <span className="nk-menu-icon">
                              <em className="icon ni ni-package" />
                            </span>
                          </Link>
                        </li>
                        <li
                          data-tip="Complementos"
                          className={`nk-menu-item ${
                            path === '/complements' && 'active'
                          }`}
                        >
                          <Link
                            to="/complements"
                            className="nk-menu-link"
                            title="Complementos"
                          >
                            <span className="nk-menu-icon">
                              <em className="icon ni ni-grid-plus" />
                            </span>
                          </Link>
                        </li>
                        <li
                          data-tip="Insumos"
                          className={`nk-menu-item ${
                            path === '/supplies' && 'active'
                          }`}
                        >
                          <Link
                            to="/supplies"
                            className="nk-menu-link"
                            title="Insumos"
                          >
                            <span className="nk-menu-icon">
                              <em className="icon ni ni-archived" />
                            </span>
                          </Link>
                        </li>
                        <li
                          data-tip="Entrega Delivery"
                          className={`nk-menu-item ${
                            path === '/distances' && 'active'
                          }`}
                        >
                          <Link
                            to="/distances"
                            className="nk-menu-link"
                            title="Entrega Delivery"
                          >
                            <span className="nk-menu-icon">
                              <em className="icon ni ni-location" />
                            </span>
                          </Link>
                        </li>
                        <li
                          data-tip="Banners"
                          className={`nk-menu-item ${
                            path === '/banners' && 'active'
                          }`}
                        >
                          <Link
                            to="/banners"
                            className="nk-menu-link"
                            title="Banners"
                          >
                            <span className="nk-menu-icon">
                              <em className="icon ni ni-img" />
                            </span>
                          </Link>
                        </li>
                        <li
                          data-tip="Mesas"
                          className={`nk-menu-item ${
                            path === '/tables' && 'active'
                          }`}
                        >
                          <Link
                            to="/tables"
                            className="nk-menu-link"
                            title="Mesas"
                          >
                            <span className="nk-menu-icon">
                              <em className="icon ni ni-box-view" />
                            </span>
                          </Link>
                        </li>
                        <li
                          data-tip="Clientes"
                          className={`nk-menu-item ${
                            path === '/clients' && 'active'
                          }`}
                        >
                          <Link
                            to="/clients"
                            className="nk-menu-link"
                            title="Clientes"
                          >
                            <span className="nk-menu-icon">
                              <em className="icon ni ni-users" />
                            </span>
                          </Link>
                        </li>
                        {REACT_APP_CHATBOT_WPP && (
                          <li
                            data-tip="Divulgador"
                            className={`nk-menu-item ${
                              path === '/promoter' && 'active'
                            }`}
                          >
                            <Link
                              to="/promoter"
                              className="nk-menu-link"
                              title="Divulgador"
                            >
                              <span className="nk-menu-icon">
                                <em className="icon ni ni-notice" />
                              </span>
                            </Link>
                          </li>
                        )}
                        <li
                          data-tip="Entregadores"
                          className={`nk-menu-item ${
                            path === '/deliverymen' && 'active'
                          }`}
                        >
                          <Link
                            to="/deliverymen"
                            className="nk-menu-link"
                            title="Entregadores"
                          >
                            <span className="nk-menu-icon">
                              <em className="icon ni ni-truck" />
                            </span>
                          </Link>
                        </li>
                        <li
                          data-tip="Cupons"
                          className={`nk-menu-item ${
                            path === '/coupons' && 'active'
                          }`}
                        >
                          <Link
                            to="/coupons"
                            className="nk-menu-link"
                            title="Cupons"
                          >
                            <span className="nk-menu-icon">
                              <em className="icon ni ni-ticket-plus" />
                            </span>
                          </Link>
                        </li>
                        <li data-tip="Meu Aplicativo" className="nk-menu-item">
                          <Link
                            to={`/${company.app_domain}`}
                            className="nk-menu-link"
                            title="Meu Aplicativo"
                            target="_blank"
                          >
                            <span className="nk-menu-icon">
                              <em className="icon ni ni-mobile" />
                            </span>
                          </Link>
                        </li>
                        <li
                          data-tip="Resumo Diário"
                          className={`nk-menu-item ${
                            path === '/resume' && 'active'
                          }`}
                        >
                          <Link
                            to="/resume"
                            className="nk-menu-link"
                            title="Resumo Diário"
                          >
                            <span className="nk-menu-icon">
                              <em className="icon ni ni-report-profit" />
                            </span>
                          </Link>
                        </li>
                        <li
                          data-tip="Configurações"
                          className={`nk-menu-item ${
                            path === '/settings' && 'active'
                          }`}
                        >
                          <Link
                            to="/settings"
                            className="nk-menu-link"
                            title="Configurações"
                          >
                            <span className="nk-menu-icon">
                              <em className="icon ni ni-setting" />
                            </span>
                          </Link>
                        </li>
                      </>
                    )}

                    {/* <li className="nk-menu-item">
                      <a
                        target="_blank"
                        href="https://web.whatsapp.com/"
                        rel="noopener noreferrer"
                        className="nk-menu-link"
                        title="Whatsapp Web"
                      >
                        <span className="nk-menu-icon">
                          <em className="icon ni ni-whatsapp" />
                        </span>
                      </a>
                    </li> */}
                  </ul>
                </div>
                {/* <div className="nk-sidebar-footer">
                  <ul className="nk-menu nk-menu-md">
                    <li className="nk-menu-item">
                      <Link
                        to="/settings"
                        className="nk-menu-link"
                        title="Settings"
                      >
                        <span className="nk-menu-icon">
                          <em className="icon ni ni-setting" />
                        </span>
                      </Link>
                    </li>
                  </ul>
                </div> */}
              </div>
              {/* <div
                className="nk-sidebar-profile nk-sidebar-profile-fixed"
                style={{ display: 'flex', justifyContent: 'center' }}
              >
                <ul className="nk-menu nk-menu-md">
                  {company.app_domain && (
                    <li className="nk-menu-item">
                      <Link
                        to={`/${company.app_domain}`}
                        target="_blank"
                        className="nk-menu-link"
                        title="Meu Aplicativo"
                      >
                        <span className="nk-menu-icon">
                          <em className="icon ni ni-mobile" />
                        </span>
                      </Link>
                    </li>
                  )}

                  <li className="nk-menu-item">
                    <Link
                      to="/resume"
                      className="nk-menu-link"
                      title="Resumo Diário"
                    >
                      <span className="nk-menu-icon">
                        <em className="icon ni ni-report-profit" />
                      </span>
                    </Link>
                  </li>
                  <li className="nk-menu-item">
                    <Link
                      to="/settings"
                      className="nk-menu-link"
                      title="Settings"
                    >
                      <span className="nk-menu-icon">
                        <em className="icon ni ni-setting" />
                      </span>
                    </Link>
                  </li>
                </ul>
              </div> */}
            </div>
          </div>
        </div>
        <div className="nk-main ">
          <div className="nk-wrap ">
            <div className="nk-header nk-header-fixed is-light">
              <div className="container-fluid">
                <div className="nk-header-wrap">
                  <div className="nk-header-app-name">
                    <div className="nk-header-app-logo" style={{ width: 'auto', height: 'auto', padding: '4px' }}>
                      <img
                        src={`${process.env.PUBLIC_URL}/fatiaprime-logo/res/mipmap-xxxhdpi/ic_launcher.png`}
                        alt="Fatia Prime"
                        style={{ width: 56, height: 56, borderRadius: 12, objectFit: 'cover', display: 'block' }}
                      />
                    </div>
                    <div className="nk-header-app-info">
                      {/* <span className="sub-text">DashLite</span> */}
                      <span className="lead-text">
                        {process.env.REACT_APP_APP_NAME}
                      </span>
                    </div>
                  </div>

                  <div className="nk-header-tools">
                    <ul className="nk-quick-nav">
                      {user.isAdmin && <CashButton />}
                      <li>
                        <div
                          role="button"
                          className="nk-quick-nav-icon d-flex flex-column align-items-center"
                          onClick={isAdmin ? handleCompanyStatus : () => {}}
                        >
                          <em
                            className={`icon ni ni-toggle-${
                              company.opened ? 'on text-success' : 'off'
                            }`}
                          />
                          <span className="fs-10px" style={{ marginTop: -6 }}>
                            {company.opened ? 'Aberto' : 'Fechado'}
                          </span>
                        </div>
                      </li>
                      <li className="dropdown notification-dropdown">
                        <Notifications />
                      </li>
                      {REACT_APP_CHATBOT_WPP && (
                        <li className="dropdown notification-dropdown">
                          <ChatbotWhatsAppSetup />
                        </li>
                      )}
                      {isAdmin && (
                        <li className="dropdown list-apps-dropdown d-lg-none">
                          <Link
                            to="#menu"
                            className="dropdown-toggle nk-quick-nav-icon"
                            data-toggle="dropdown"
                          >
                            <div className="icon-status icon-status-na">
                              <em className="icon ni ni-menu-circled" />
                            </div>
                          </Link>
                          <div className="dropdown-menu dropdown-menu-lg dropdown-menu-right">
                            <div className="dropdown-body">
                              <ul className="list-apps">
                                {/* <li>
                                <Link to="/">
                                  <span className="list-apps-media">
                                    <em className="icon ni ni-dashboard" />
                                  </span>
                                  <span className="list-apps-title">
                                    Dashboard
                                  </span>
                                </Link>
                              </li> */}
                                <li>
                                  <Link to="/orders">
                                    <span className="list-apps-media">
                                      <em className="icon ni ni-invest" />
                                    </span>
                                    <span className="list-apps-title">
                                      Pedidos
                                    </span>
                                  </Link>
                                </li>
                                {company.sell_on_credit === '1' && (
                                  <li>
                                    <Link to="/buyoncredit">
                                      <span className="list-apps-media">
                                        <em className="icon ni ni-invest" />
                                      </span>
                                      <span className="list-apps-title">
                                        Fiados
                                      </span>
                                    </Link>
                                  </li>
                                )}
                                <li>
                                  <Link to="/table-orders">
                                    <span className="list-apps-media">
                                      <em className="icon ni ni-clipboad-check" />
                                    </span>
                                    <span className="list-apps-title">
                                      Pedidos de Mesa
                                    </span>
                                  </Link>
                                </li>

                                <li>
                                  <Link to="/menus">
                                    <span className="list-apps-media">
                                      <em className="icon ni ni-todo" />
                                    </span>
                                    <span className="list-apps-title">
                                      Cardápios
                                    </span>
                                  </Link>
                                </li>

                                <li>
                                  <Link to="/categories">
                                    <span className="list-apps-media">
                                      <em className="icon ni ni-tags" />
                                    </span>
                                    <span className="list-apps-title">
                                      Categorias
                                    </span>
                                  </Link>
                                </li>
                                <li>
                                  <Link to="/products">
                                    <span className="list-apps-media">
                                      <em className="icon ni ni-package" />
                                    </span>
                                    <span className="list-apps-title">
                                      Produtos
                                    </span>
                                  </Link>
                                </li>

                                <li>
                                  <Link to="/complements">
                                    <span className="list-apps-media">
                                      <em className="icon ni ni-grid-plus" />
                                    </span>
                                    <span className="list-apps-title">
                                      Complementos
                                    </span>
                                  </Link>
                                </li>

                                <li>
                                  <Link to="/supplies">
                                    <span className="list-apps-media">
                                      <em className="icon ni ni-archived" />
                                    </span>
                                    <span className="list-apps-title">
                                      Insumos
                                    </span>
                                  </Link>
                                </li>

                                <li>
                                  <Link to="/distances">
                                    <span className="list-apps-media">
                                      <em className="icon ni ni-location" />
                                    </span>
                                    <span className="list-apps-title">
                                      Entrega Delivery
                                    </span>
                                  </Link>
                                </li>
                                <li>
                                  <Link to="/banners">
                                    <span className="list-apps-media">
                                      <em className="icon ni ni-img" />
                                    </span>
                                    <span className="list-apps-title">
                                      Banners
                                    </span>
                                  </Link>
                                </li>
                                <li>
                                  <Link to="/coupons">
                                    <span className="list-apps-media">
                                      <em className="icon ni ni-ticket-plus" />
                                    </span>
                                    <span className="list-apps-title">
                                      Cupons
                                    </span>
                                  </Link>
                                </li>
                                <li>
                                  <Link to="/tables">
                                    <span className="list-apps-media">
                                      <em className="icon ni ni-box-view" />
                                    </span>
                                    <span className="list-apps-title">
                                      Mesas
                                    </span>
                                  </Link>
                                </li>
                                <li>
                                  <Link to="/clients">
                                    <span className="list-apps-media">
                                      <em className="icon ni ni-users" />
                                    </span>
                                    <span className="list-apps-title">
                                      Clientes
                                    </span>
                                  </Link>
                                </li>
                                {REACT_APP_CHATBOT_WPP && (
                                  <li>
                                    <Link to="/promoter">
                                      <span className="list-apps-media">
                                        <em className="icon ni ni-notice" />
                                      </span>
                                      <span className="list-apps-title">
                                        Divulgador
                                      </span>
                                    </Link>
                                  </li>
                                )}
                                <li>
                                  <Link to="/deliverymen">
                                    <span className="list-apps-media">
                                      <em className="icon ni ni-truck" />
                                    </span>
                                    <span className="list-apps-title">
                                      Entregadores
                                    </span>
                                  </Link>
                                </li>
                                <li>
                                  <a
                                    target="_blank"
                                    href="https://web.whatsapp.com/"
                                    rel="noopener noreferrer"
                                  >
                                    <span className="list-apps-media">
                                      <em className="icon ni ni-whatsapp" />
                                    </span>
                                    <span className="list-apps-title">
                                      Whatsapp
                                    </span>
                                  </a>
                                </li>
                                <li>
                                  <Link to="/resume">
                                    <span className="list-apps-media">
                                      <em className="icon ni ni-report-profit" />
                                    </span>
                                    <span className="list-apps-title">
                                      Resumo Diário
                                    </span>
                                  </Link>
                                </li>
                                {company.app_domain && (
                                  <li>
                                    <Link
                                      to={`/${company.app_domain}`}
                                      target="_blank"
                                    >
                                      <span className="list-apps-media">
                                        <em className="icon ni ni-mobile" />
                                      </span>
                                      <span className="list-apps-title">
                                        Meu Aplicativo
                                      </span>
                                    </Link>
                                  </li>
                                )}
                              </ul>
                            </div>
                          </div>
                        </li>
                      )}
                      <li className="dropdown user-dropdown">
                        <Link
                          to="#profile"
                          className="dropdown-toggle mr-n1"
                          data-toggle="dropdown"
                        >
                          <div className="user-toggle">
                            <div className="user-avatar sm">
                              <em className="icon ni ni-user-alt" />
                            </div>
                          </div>
                        </Link>
                        <div className="dropdown-menu dropdown-menu-md dropdown-menu-right">
                          <div className="dropdown-inner user-card-wrap bg-lighter d-none d-md-block">
                            <div className="user-card">
                              <div className="user-avatar">
                                <span>{initials}</span>
                              </div>
                              <div className="user-info">
                                <span className="lead-text">{user.name}</span>
                                <span className="sub-text">{user.email}</span>
                              </div>
                            </div>
                          </div>
                          <div className="dropdown-inner">
                            <ul className="link-list">
                              <li>
                                <Link to="/settings">
                                  <em className="icon ni ni-setting-alt" />
                                  <span>Configurações</span>
                                </Link>
                              </li>
                            </ul>
                          </div>
                          <div className="dropdown-inner">
                            <ul className="link-list">
                              <li>
                                <Link to="#signout" onClick={handleOnSignOut}>
                                  <em className="icon ni ni-signout" />
                                  <span>Sair</span>
                                </Link>
                              </li>
                            </ul>
                          </div>
                          <div className="dropdown-inner">
                            <ul className="link-list">
                              <li>
                                <b>{`Versão: ${packageJson.version}`}</b>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="nk-content ">
              <div className="container-fluid">
                <div className="nk-content-inner">
                  <div className="nk-content-body">
                    {dueDateDays >= 0 && dueDateDays <= 7 && (
                      <div className={`mb-3 ${containerMessage}`}>
                        <div className="alert alert-warning alert-icon">
                          <em className="icon ni ni-alert-circle" />
                          <strong className="me-2">Atenção!</strong>
                          {dueDateDays === 0 && (
                            <span className="me-2">
                              A sua mensalidade vence hoje.
                            </span>
                          )}

                          {dueDateDays === 1 && (
                            <span className="me-2">
                              {`Falta ${dueDateDays} dia para o vencimento.`}
                            </span>
                          )}

                          {dueDateDays > 1 && (
                            <span className="me-2">
                              {`Faltam ${dueDateDays} dias para o vencimento.`}
                            </span>
                          )}

                          <b
                            className="alert-link"
                            onClick={handleGoToPayments}
                            style={{ cursor: 'pointer' }}
                          >
                            Realizar Pagamento.
                          </b>
                        </div>
                      </div>
                    )}

                    {dueDateDays < 0 && (
                      <div className={`mb-3 ${containerMessage}`}>
                        <div className="alert alert-danger alert-icon">
                          <em className="icon ni ni-alert-circle" />
                          <strong className="me-2">Atenção!</strong>

                          <span className="me-2">
                            A sua mensalidade venceu.
                          </span>

                          <b
                            className="alert-link"
                            onClick={handleGoToPayments}
                            style={{ cursor: 'pointer' }}
                          >
                            Realizar Pagamento.
                          </b>
                        </div>
                      </div>
                    )}

                    {children}

                    {/* <NewsInfoModal ref={newInfoModalRef} /> */}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* <InstallPWAButton position="right" /> */}
    </>
  );
};;

export default Dashboard;
